import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Upload.css";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const MAX_IMAGE_DIMENSION = 250; // Maximum width or height

function Upload({ credentials, onUploadSuccess }) {
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!credentials?.username) {
      navigate("/login", {
        state: { message: "You must be logged in to upload maps" },
      });
    }
  }, [credentials, navigate]);

  const validateAndOptimizeImage = async (file) => {
    if (file.size > MAX_FILE_SIZE) {
      throw new Error("File size must be less than 5MB");
    }

    const img = new Image();
    const imageUrl = URL.createObjectURL(file);

    try {
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        const ratio = Math.min(
          MAX_IMAGE_DIMENSION / width,
          MAX_IMAGE_DIMENSION / height
        );
        width *= ratio;
        height *= ratio;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      const webpBlob = await new Promise((resolve) => {
        canvas.toBlob(resolve, "image/webp", 0.9);
      });

      return new File([webpBlob], file.name.replace(/\.[^.]+$/, ".webp"), {
        type: "image/webp",
      });
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    setError("");

    if (file) {
      try {
        const optimizedFile = await validateAndOptimizeImage(file);
        setSelectedFile(optimizedFile);
      } catch (err) {
        setError(err.message);
        event.target.value = "";
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const mapName = event.target.name.value.trim();
    const mapCode = event.target.code.value.trim();
    const tags = event.target.tags.value.trim();

    // Validate inputs
    if (!selectedFile) {
      setError("Please select a file");
      return;
    }

    if (!mapName) {
      setError("Map name is required");
      return;
    }

    if (mapName.length > 40) {
      setError("Map name must not exceed 40 characters");
      return;
    }

    if (!mapCode) {
      setError("Map code is required");
      return;
    }

    if (tags.length > 20) {
      setError("Tags must not exceed 20 characters");
      return;
    }

    if (mapCode.length < 5 || mapCode.length > 10) {
      setError("Map code must be between 5 and 10 characters");
      return;
    }

    const formData = new FormData();
    formData.append("image", selectedFile);
    formData.append("name", mapName);
    formData.append("code", mapCode);
    formData.append("tags", tags);
    formData.append("uploader_username", credentials.username);
    formData.append("map_size", event.target.mapSize.value);
    formData.append("player_config", event.target.playerConfig.value);
    formData.append("team_positions", event.target.teamStart.value);
    formData.append("biome", event.target.biomeType.value);
    formData.append("map_type", event.target.mapType.value);

    setUploading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Basic ${btoa(
              `${credentials.username}:${credentials.password}`
            )}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Upload failed");

      onUploadSuccess();
      navigate("/");
    } catch (err) {
      setError("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  if (!credentials?.username) {
    return null;
  }

  return (
    <div className="upload-container">
      <form onSubmit={handleSubmit} className="upload-form">
        <h2>Upload Map</h2>
        <div className="file-input-container">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            required
            maxLength="20"
          />
        </div>
        <input
          type="text"
          name="name"
          placeholder="Map Name (max 20 chars)"
          className="text-input"
          maxLength="20"
          required
        />
        <input
          type="text"
          name="code"
          placeholder="Map Code (5-10 characters)"
          className="text-input"
          required
          maxLength="10"
        />
        <input
          type="text"
          name="tags"
          placeholder="Tags (max 25 chars)"
          className="text-input"
          maxLength="25"
        />
        <select name="mapSize" className="text-input" required>
          <option value="">Select Map Size</option>
          <option value="Micro">Micro</option>
          <option value="Small">Small</option>
          <option value="Medium">Medium</option>
          <option value="Large">Large</option>
          <option value="Gigantic">Gigantic</option>
        </select>
        <select name="playerConfig" className="text-input" required>
          <option value="">Select Player Configuration</option>
          <option value="1v1">1v1</option>
          <option value="2v2">2v2</option>
          <option value="3v3">3v3</option>
          <option value="4v4">4v4</option>
          <option value="ffa">FFA</option>
        </select>
        <select name="teamStart" className="text-input" required>
          <option value="">Select Team Start Positions</option>
          <option value="Random Locations">Random Locations</option>
          <option value="Teams Together">Teams Together</option>
        </select>
        <select name="biomeType" className="text-input" required>
          <option value="">Select Biome Type</option>
          <option value="Asian Subtropical">Asian Subtropical</option>
          <option value="Asian Temperate">Asian Temperate</option>
          <option value="Chalk Downs">Chalk Downs</option>
          <option value="European Temperate">European Temperate</option>
          <option value="Gobi Desert">Gobi Desert</option>
          <option value="Japanese Spring">Japanese Spring</option>
          <option value="Mediterranean">Mediterranean</option>
          <option value="Sahara Desert">Sahara Desert</option>
          <option value="Savanna">Savanna</option>
          <option value="Steppes">Steppes</option>
          <option value="Taiga Summer">Taiga Summer</option>
          <option value="Taiga Winter">Taiga Winter</option>
          <option value="winter">Winter</option>
        </select>
        <select name="mapType" className="text-input" required>
          <option value="">Select Map Type</option>
          <option value="MegaRandom">MegaRandom</option>
          <option value="land MegaRandom">Land MegaRandom</option>
          <option value="African Waters">African Waters</option>
          <option value="Altai">Altai</option>
          <option value="Ancient Spires">Ancient Spires</option>
          <option value="Archipelago">Archipelago</option>
          <option value="Atacama">Atacama</option>
          <option value="Baltic">Baltic</option>
          <option value="Black Forest">Black Forest</option>
          <option value="Boulder Bay">Boulder Bay</option>
          <option value="Canal">Canal</option>
          <option value="Cliffside">Cliffside</option>
          <option value="Confluence">Confluence</option>
          <option value="Continental">Continental</option>
          <option value="Danube River">Danube River</option>
          <option value="Dry Arabia">Dry Arabia</option>
          <option value="Forest Ponds">Forest Ponds</option>
          <option value="Four Lakes">Four Lakes</option>
          <option value="Forts">Forts</option>
          <option value="French Pass">French Pass</option>
          <option value="Glade">Glade</option>
          <option value="Golden Heights">Golden Heights</option>
          <option value="Golden Pit">Golden Pit</option>
          <option value="Gorge">Gorge</option>
          <option value="Haywire">Haywire</option>
          <option value="Hidden Valley">Hidden Valley</option>
          <option value="Hideout">Hideout</option>
          <option value="HighView">High View</option>
          <option value="Hill And Dale">Hill and Dale</option>
          <option value="Himeyama">Himeyama</option>
          <option value="king Of The Hill">King of the Hill</option>
          <option value="Lake Side">Lake Side</option>
          <option value="Lipany">Lipany</option>
          <option value="Marshland">Marshland</option>
          <option value="Migration">Migration</option>
          <option value="Mongolian Heights">Mongolian Heights</option>
          <option value="Mountain Clearing">Mountain Clearing</option>
          <option value="Mountain Pass">Mountain Pass</option>
          <option value="Nagari">Nagari</option>
          <option value="Oasis">Oasis</option>
          <option value="Prairie">Prairie</option>
          <option value="Rocky River">Rocky River</option>
          <option value="Socotra">Socotra</option>
          <option value="The Pit">The Pit</option>
          <option value="Thickets">Thickets</option>
          <option value="Turtle Ridge">Turtle Ridge</option>
          <option value="Volcanic Island">Volcanic Island</option>
          <option value="Warring Islands">Warring Islands</option>
          <option value="Waterholes">Waterholes</option>
          <option value="Wetlands">Wetlands</option>
        </select>
        <button type="submit" disabled={uploading}>
          {uploading ? "Uploading..." : "Upload"}
        </button>
        {error && <p className="error">{error}</p>}
      </form>
    </div>
  );
}

export default Upload;
